import _ from 'lodash';
import {BASE_URL} from 'appConstants';

import {transformFiltersForStoreToFiltersForApi} from 'utils/filters';
import {getFormattedDateForApiOrValue} from 'utils/datetime';
import {transformSortForStoreToSortForUrl} from 'utils/sort';

import {get, post} from 'sdk';

export const fetchBookings = ({locationRef, extraGetParams}) => {
  const url = `${BASE_URL}/v2/living/booking/${locationRef}/`;

  return get(url, extraGetParams);
};

export const fetchBooking = ({locationRef, bookingId}) => {
  const url = `${BASE_URL}/v2/living/booking/${locationRef}/${bookingId}/`;

  return get(url);
};

export const createOffer = ({
  startDate,
  endDate,
  roomDeposit,
  pwRent,
  breakClause,
  booking
}) => {
  const url = `${BASE_URL}/v2/living/booking-offer/create/`;
  const data = {
    startDate,
    endDate,
    roomDeposit,
    pwRent,
    breakClause,
    booking
  };

  return post(url, data);
};

export const fetchAgreements = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/agreements/`;
  return get(url, {booking: bookingId});
};

export const fetchAgreement = ({agreementId}) => {
  const url = `${BASE_URL}/v1/living/agreements/${agreementId}/`;
  return get(url);
};

export const fetchSendAgreementDetails = ({agreementId}) => {
  const url = `${BASE_URL}/v1/living/agreements/send-details/${agreementId}/`;
  return get(url);
};

export const fetchAgreementTemplates = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/agreements/templates/${bookingId}/`;
  return get(url);
};

export const fetchKycs = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/kycs/`;

  return get(url);
};

export const fetchAffordabilities = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/affordabilities/`;

  return get(url);
};

export const fetchLegacyDocuments = ({bookingId}) => {
  const url = `${BASE_URL}/v2/living/documents/legacy/${bookingId}/`;

  return get(url);
};

export const addAgreement = ({agreementTemplate, bookingId}) => {
  const url = `${BASE_URL}/v1/living/agreements/add/`;
  const payload = {agreementTemplate, booking: bookingId};

  return post(url, payload);
};

export const sendAgreement = ({agreementId, warnings}) => {
  const url = `${BASE_URL}/v1/living/agreements/send/`;
  return post(url, {agreement: agreementId, warnings});
};

export const sendAgreementReminder = ({agreement, agreementUsers}) => {
  const url = `${BASE_URL}/v1/living/agreements/send-signature-reminders/`;
  const payload = {agreement, agreementUsers};
  return post(url, payload);
};

export const fetchSendAgreementReminderDetails = ({agreement}) => {
  const url = `${BASE_URL}/v1/living/agreements/send-signature-reminders-detail/${agreement}/`;

  return get(url);
};

export const cancelAgreement = ({agreement}) => {
  const url = `${BASE_URL}/v1/living/agreements/cancel-signature-request/`;
  const payload = {agreement};
  return post(url, payload);
};

export const signAgreementManually = ({agreement}) => {
  const url = `${BASE_URL}/v1/living/agreements/sign-manually/`;
  const payload = {agreement};
  return post(url, payload);
};

export const fetchRentSchedule = ({bookingId}) => {
  const url = `${BASE_URL}/v1/payments/schedule/rent-retrieve/${bookingId}/`;

  return get(url);
};

export const fetchLegacyTypes = () => {
  const url = `${BASE_URL}/v2/living/documents/legacy-types/`;
  return get(url);
};

export const createLegacyDocument = ({
  identifier,
  legacyType,
  title,
  notes,
  booking
}) => {
  const url = `${BASE_URL}/v2/living/documents/legacy/verify-upload/`;
  const payload = {identifier, legacyType, title, notes, booking};

  return post(url, payload);
};

export const verifyKycDocumentsUpload = ({poid, poa, visa, kyc}) => {
  const url = `${BASE_URL}/v2/living/documents/kyc/verify-upload/`;
  const payload = {poid, poa, visa, kyc};

  return post(url, payload);
};

export const deleteKycDocument = ({kycDocumentId, kyc}) => {
  const url = `${BASE_URL}/v2/living/documents/kyc/delete/`;
  const payload = {identifier: kycDocumentId, kyc};

  return post(url, payload);
};

export const deleteAffordabilityDocument = ({documentId}) => {
  const url = `${BASE_URL}/v2/living/documents/affordability/delete/`;
  const payload = {identifier: documentId};

  return post(url, payload);
};

export const legacyDocumentSign = ({filename, fileType, owner}) => {
  const url = `${BASE_URL}/v2/living/documents/legacy/sign/`;
  const payload = {filename, fileType, owner};

  return post(url, payload);
};

export const signKycIdentificationDocument = ({filename, fileType, owner}) => {
  const url = `${BASE_URL}/v2/living/documents/kyc/sign/poi/`;
  const payload = {filename, fileType, owner};

  return post(url, payload);
};

export const signKycAddressDocument = ({filename, fileType, owner}) => {
  const url = `${BASE_URL}/v2/living/documents/kyc/sign/poa/`;
  const payload = {filename, fileType, owner};

  return post(url, payload);
};

export const signKycVisaDocument = ({filename, fileType, owner}) => {
  const url = `${BASE_URL}/v2/living/documents/kyc/sign/visa/`;
  const payload = {filename, fileType, owner};

  return post(url, payload);
};

export const searchTenants = ({value}) => {
  const url = `${BASE_URL}/v1/living/tenant/`;
  const params = {search: value};

  return get(url, params);
};

export const searchAgreementUsers = ({value}) => {
  const url = `${BASE_URL}/v1/living/agreement-user/`;
  const params = {search: value};

  return get(url, params);
};

export const createBooking = ({
  locationRef,
  roomType,
  primaryTenant,
  secondaryTenant,
  pwRent,
  roomDeposit,
  startDate,
  endDate,
  room,
  createCleanupPeriod,
  cleanupPeriodDays,
  bookingType,
  bookingOrganisation,
  bookingSet
}) => {
  const url = `${BASE_URL}/v1/living/booking/${locationRef}/create/`;
  const payload = {
    roomType,
    primaryTenant,
    secondaryTenant,
    pwRent,
    roomDeposit,
    startDate,
    endDate,
    room,
    createCleanupPeriod,
    cleanupPeriodDays,
    bookingType,
    bookingOrganisation,
    bookingSet
  };

  return post(url, payload);
};

export const createBookingExtension = ({
  existingBooking,
  room,
  roomType,
  startDate,
  endDate,
  primaryTenant,
  secondaryTenant,
  pwRent,
  roomDeposit,
  createCleanupPeriod,
  cleanupPeriodDays
}) => {
  const url = `${BASE_URL}/v1/living/booking/create-extension/`;
  const payload = {
    existingBooking,
    room,
    roomType,
    startDate,
    endDate,
    primaryTenant,
    secondaryTenant,
    pwRent,
    roomDeposit,
    createCleanupPeriod,
    cleanupPeriodDays
  };

  return post(url, payload);
};

export const fetchAffordability = ({bookingId, affordabilityId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/affordability/${affordabilityId}/`;

  return get(url);
};

export const affordabilityDocumentSign = ({filename, fileType, owner}) => {
  const url = `${BASE_URL}/v2/living/documents/affordability/sign/`;
  const payload = {filename, fileType, owner};

  return post(url, payload);
};

export const verifyAffordabilityDocumentUpload = ({identifier}) => {
  const url = `${BASE_URL}/v2/living/documents/affordability/verify-upload/`;
  const payload = {identifier};

  return post(url, payload);
};

export const verifyKyc = ({bookingId, kycId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/kyc/${kycId}/verify/`;

  return post(url);
};

export const verifyIdentity = ({bookingId, kycId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/kyc/${kycId}/verify-identity/`;

  return post(url);
};

export const verifyAddress = ({bookingId, addressHistoryId, kycId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/kyc/${kycId}/verify-address/`;

  const payload = {address: addressHistoryId};

  return post(url, payload);
};

export const verifyVisa = ({bookingId, kycId, expiryDate}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/kyc/${kycId}/verify-visa/`;
  const payload = {expiryDate};

  return post(url, payload);
};

export const fetchKycDetails = ({kycId, bookingId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/kyc/${kycId}/`;

  return get(url);
};

export const fetchKycSectionDetails = ({kycId, bookingId, sectionId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/kyc/${kycId}/sections/${sectionId}/`;

  return get(url);
};

export const confirmCurrentAddress = ({agreementUserId, addressHistoryId}) => {
  const url = `${BASE_URL}/v1/living/agreement-user/${agreementUserId}/address/${addressHistoryId}/confirm-current/`;

  return post(url);
};

export const generateRentSchedule = ({
  booking,
  billingDate,
  billingFrequency,
  invoicesToChargeBeforeMoveIn,
  preArrivalInvoicesDue,
  rentPerMonth
}) => {
  const url = `${BASE_URL}/v1/payments/schedule/generate/`;
  const payload = {
    booking,
    billingDate,
    billingFrequency,
    invoicesToChargeBeforeMoveIn,
    preArrivalInvoicesDue,
    rentPerMonth
  };

  return post(url, payload);
};

export const applyRentSchedule = ({
  booking,
  billingDate,
  billingFrequency,
  invoicesToChargeBeforeMoveIn,
  preArrivalInvoicesDue,
  rentPerMonth
}) => {
  const url = `${BASE_URL}/v1/payments/schedule/apply-rent/`;
  const payload = {
    booking,
    billingDate,
    billingFrequency,
    invoicesToChargeBeforeMoveIn,
    preArrivalInvoicesDue,
    rentPerMonth
  };

  return post(url, payload);
};

export const requestKycDocument = ({
  title,
  message,
  kycSection,
  kycId,
  bookingId
}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/kyc/${kycId}/request-documents/`;
  const payload = {title, message, kycSection};
  return post(url, payload);
};

export const addGuarantor = ({bookingId, fullName, email}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/guarantor/add/`;
  const payload = {
    fullName,
    email
  };

  return post(url, payload);
};

export const removeGuarantor = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/guarantor/remove/`;

  return post(url);
};

export const addSecondaryTenant = ({bookingId, fullName, email}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/secondary-tenant/add/`;
  const payload = {fullName, email};

  return post(url, payload);
};

export const removeSecondaryTenant = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/secondary-tenant/remove/`;

  return post(url);
};

export const fetchRentScheduleSummary = ({
  bookingId,
  rentFrequency,
  rentPerMonth
}) => {
  const url = `${BASE_URL}/v1/payments/schedule/rent-schedule-summary/`;
  const payload = {
    booking: bookingId,
    rentFrequency,
    rentPerMonth
  };

  return post(url, payload);
};

export const passAffordability = ({bookingId, locationRef}) => {
  const url = `${BASE_URL}/v2/living/booking/${locationRef}/${bookingId}/pass-affordability/`;

  return post(url);
};

export const fetchBookingTypes = () => {
  const url = `${BASE_URL}/v1/living/booking/types/`;

  return get(url);
};

export const fetchBookingOrganisations = () => {
  const url = `${BASE_URL}/v1/living/booking/organisations/`;

  return get(url);
};

export const addEmployment = ({
  agreementUserId,
  unemployed,
  name,
  title,
  website,
  salary,
  startDate,
  bookingId
}) => {
  const url = `${BASE_URL}/v1/living/agreement-user/${agreementUserId}/add-employment/`;

  const payload = {
    unemployed,
    name,
    title,
    website,
    salary,
    startDate,
    bookingId
  };

  return post(url, payload);
};

export const exportBookingsCsvInBackground = ({filters, locationRef, sort}) => {
  const url = `${BASE_URL}/v2/living/booking/${locationRef}/export-csv/bookings/in-background/`;

  const normalizedFilters = _.mapValues(filters, getFormattedDateForApiOrValue);

  const params = {
    ...transformFiltersForStoreToFiltersForApi(normalizedFilters),
    ...transformSortForStoreToSortForUrl(sort)
  };

  return get(url, params);
};

export const exportBookingsWithoutFinancialDataCsvInBackground = ({
  filters,
  locationRef,
  sort
}) => {
  const url = `${BASE_URL}/v2/living/booking/${locationRef}/export-csv/bookings-without-financial-data/in-background/`;

  const normalizedFilters = _.mapValues(filters, getFormattedDateForApiOrValue);

  const params = {
    ...transformFiltersForStoreToFiltersForApi(normalizedFilters),
    ...transformSortForStoreToSortForUrl(sort)
  };

  return get(url, params);
};

export const exportContactsCsv = ({filters, locationRef}) => {
  const url = `${BASE_URL}/v2/living/booking/${locationRef}/export-csv/contacts/`;

  const normalizedFilters = _.mapValues(filters, getFormattedDateForApiOrValue);

  const params = {
    ...transformFiltersForStoreToFiltersForApi(normalizedFilters)
  };

  return get(url, params);
};

export const exportContactsCsvInBackground = ({filters, locationRef}) => {
  const url = `${BASE_URL}/v2/living/booking/${locationRef}/export-csv/contacts/in-background/`;

  const normalizedFilters = _.mapValues(filters, getFormattedDateForApiOrValue);

  const params = {
    ...transformFiltersForStoreToFiltersForApi(normalizedFilters)
  };

  return get(url, params);
};

export const cancelBooking = ({bookingId, cancelledReason}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/cancel/`;

  const payload = {
    cancelledReason
  };
  return post(url, payload);
};

export const fetchBookingListFilters = ({locationRef}) => {
  const url = `${BASE_URL}/v1/living/booking/filters/`;

  return get(url, {location: locationRef});
};

export const fetchBookingOptions = ({locationRef, search}) => {
  const url = `${BASE_URL}/v1/living/booking/extension-modal/list/`;

  return get(url, {search, location: locationRef});
};

export const fetchSalesAgents = ({locationRef}) => {
  const url = `${BASE_URL}/v1/living/booking/${locationRef}/sales-agents/`;

  return get(url);
};

export const fetchGoCardlessMandateStatuses = () => {
  const url = `${BASE_URL}/v1/payments/gocardless/mandate-statuses/`;

  return get(url);
};

export const fetchGoCardlessInvitationStatuses = () => {
  const url = `${BASE_URL}/v1/payments/gocardless/invitation-statuses/`;

  return get(url);
};

export const deleteLegacyDocument = ({legacyDocumentId}) => {
  const url = `${BASE_URL}/v2/living/documents/legacy/${legacyDocumentId}/delete/`;

  return post(url);
};

export const editLegacyDocument = ({title, legacyType, notes, documentId}) => {
  const url = `${BASE_URL}/v2/living/documents/legacy/${documentId}/edit/`;

  const payload = {
    title,
    legacyType,
    notes
  };

  return post(url, payload);
};

export const fetchCreateOfferWarnings = ({bookingId}) => {
  const url = `${BASE_URL}/v2/living/booking-offer/create-warnings/`;

  const params = {
    booking: bookingId
  };
  return get(url, params);
};

export const createAddressHistory = ({
  agreementUserId,
  street,
  city,
  country,
  state,
  postalCode,
  current,
  start,
  end,
  location
}) => {
  const url = `${BASE_URL}/v1/living/agreement-user/${agreementUserId}/add-address/`;

  const payload = {
    street,
    city,
    country,
    state,
    postalCode,
    current,
    start,
    end,
    location
  };

  return post(url, payload);
};

export const fetchActivityLog = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/activity-log/`;

  return get(url);
};

export const createOneOffChargeInvoiceAndPayment = ({
  bookingId,
  label,
  description,
  amount,
  createPayment,
  paymentMethod,
  paymentDate,
  invoiceDueDate,
  reference,
  xeroAccount,
  batchNumber,
  creditCardNumber,
  oneOffChargeReasonId
}) => {
  const url = `${BASE_URL}/v1/payments/one-off-charges/${bookingId}/create-invoice-and-payment/`;

  const payload = {
    label,
    description,
    amount,
    createPayment,
    paymentDate,
    invoiceDueDate,
    paymentMethod,
    reference,
    xeroAccount,
    batchNumber,
    creditCardNumber,
    oneOffChargeReasonId
  };

  return post(url, payload);
};

export const checkOutTenantsEarly = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/check-out-tenants-early/`;

  return post(url);
};

export const undoCheckOutTenantsEarly = ({bookingId}) => {
  const url = `${BASE_URL}/v1/living/booking/${bookingId}/undo-check-out-tenants-early/`;

  return post(url);
};

export const fetchBookingsForBookingTracker = ({
  locationRef,
  extraGetParams
}) => {
  const url = `${BASE_URL}/v2/living/booking/${locationRef}/list/booking-tracker/`;

  return get(url, extraGetParams);
};
